import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Form, Input, message, Modal, Popconfirm, Select, Space, Table, Tag, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { api } from "../services/apiClient";
import { useSessionProfile } from "../services/SessionProfileContext";

const { Title, Text } = Typography;
const GLOBAL_OWNER = "__GLOBAL__";

function ownerLabel(item) {
  if (!item?.userId) return "Hệ thống (toàn cục)";
  return item.username || item.userEmail || item.userId;
}

function normalizePayload(values, isSuperAdmin) {
  return {
    userId: isSuperAdmin ? (values.userId === GLOBAL_OWNER ? null : values.userId) : undefined,
    parType: values.parType?.trim(),
    parCode: values.parCode?.trim(),
    parName: values.parName?.trim(),
    parValue: values.parValue?.trim() || "",
    description: values.description?.trim() || "",
  };
}

export default function SystemAppParams() {
  const { profile, status } = useSessionProfile();
  const isSuperAdmin = status === "ready" && profile.role === "SUPER_ADMIN";
  const mounted = useRef(true);
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState();
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = await api.get(`/api/app-params${isSuperAdmin && selectedUserId ? `?userId=${encodeURIComponent(selectedUserId)}` : ""}`);
      if (!mounted.current) return;
      setItems(Array.isArray(params) ? params : []);
    } catch (error) {
      if (mounted.current) message.error(error.message);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [isSuperAdmin, selectedUserId]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let active = true;
    api.get("/api/users/options")
      .then((options) => { if (active) setUsers(Array.isArray(options) ? options : []); })
      .catch((error) => { if (active) message.error(error.message); });
    return () => { active = false; };
  }, [isSuperAdmin]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    if (isSuperAdmin) form.setFieldsValue({ userId: profile.id || GLOBAL_OWNER });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    form.setFieldsValue({
      userId: item.userId || GLOBAL_OWNER,
      parType: item.parType,
      parCode: item.parCode,
      parName: item.parName,
      parValue: item.parValue,
      description: item.description,
    });
    setModalOpen(true);
  };

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = normalizePayload(values, isSuperAdmin);
      if (editing) await api.put(`/api/app-params/${editing.id}`, payload);
      else await api.post("/api/app-params", payload);
      message.success(editing ? "Đã cập nhật tham số" : "Đã tạo tham số");
      setModalOpen(false);
      await load();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message);
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/api/app-params/${item.id}`);
      message.success("Đã xóa tham số");
      await load();
    } catch (error) {
      message.error(error.message);
    }
  };

  const data = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return items;
    return items.filter((item) => [ownerLabel(item), item.parType, item.parCode, item.parName, item.parValue, item.description]
      .filter(Boolean).some((value) => String(value).toLowerCase().includes(keyword)));
  }, [items, search]);

  const columns = [
    ...(isSuperAdmin ? [{ title: "NGƯỜI DÙNG", width: 190, className: "app-param-wrap-cell", render: (_, item) => item.userId ? <div><Text strong>{item.username || item.userId}</Text><br /><Text type="secondary">{item.userEmail || ""}</Text></div> : <Tag color="purple">HỆ THỐNG TOÀN CỤC</Tag> }] : []),
    { title: "LOẠI THAM SỐ", dataIndex: "parType", width: 150, className: "app-param-wrap-cell", render: (value) => <Tag color="blue">{value}</Tag> },
    { title: "MÃ THAM SỐ", dataIndex: "parCode", width: 170, className: "app-param-wrap-cell", render: (value) => <Text code>{value}</Text> },
    { title: "TÊN THAM SỐ", dataIndex: "parName", width: 190, className: "app-param-wrap-cell" },
    { title: "GIÁ TRỊ THAM SỐ", dataIndex: "parValue", className: "app-param-wrap-cell", render: (value) => value || <Text type="secondary">Trống</Text> },
    { title: "MÔ TẢ", dataIndex: "description", className: "app-param-wrap-cell", render: (value) => value || "-" },
    { title: "THAO TÁC", width: 96, fixed: "right", render: (_, item) => <Space size={0}><Button type="text" aria-label={`Sửa ${item.parCode}`} size="small" icon={<EditOutlined />} onClick={() => openEdit(item)} /><Popconfirm title="Xóa tham số này?" okText="Xóa" cancelText="Hủy" onConfirm={() => remove(item)}><Button type="text" aria-label={`Xóa ${item.parCode}`} danger size="small" icon={<DeleteOutlined />} /></Popconfirm></Space> },
  ];

  const userOptions = [
    { value: GLOBAL_OWNER, label: "Hệ thống (toàn cục)" },
    ...users.map((user) => ({ value: user.id, label: `${user.username || user.email || user.id}${user.email ? ` (${user.email})` : ""}` })),
  ];

  return <>
    <div className="page-heading"><div><Title level={3}>Tham số ứng dụng</Title><Text type="secondary">Quản lý cấu hình ứng dụng theo từng người dùng.</Text></div></div>
    <Card className="criclebox">
      <div className="auto-toolbar">
        <Input className="auto-toolbar-search" allowClear prefix={<SearchOutlined />} placeholder="Tìm theo loại, mã, tên hoặc giá trị" value={search} onChange={(event) => setSearch(event.target.value)} />
        {isSuperAdmin && <Select aria-label="Lọc theo người dùng" allowClear showSearch optionFilterProp="label" placeholder="Tất cả người dùng" style={{ minWidth: 240 }} value={selectedUserId} onChange={setSelectedUserId} options={users.map((user) => ({ value: user.id, label: `${user.username || user.email || user.id}${user.email ? ` (${user.email})` : ""}` }))} />}
        <Button icon={<ReloadOutlined />} onClick={load}>Làm mới</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Thêm tham số</Button>
      </div>
      <div className="table-responsive"><Table className="app-param-table" rowKey="id" loading={loading} columns={columns} dataSource={data} scroll={{ x: 1100 }} /></div>
    </Card>
    <Modal title={editing ? "Cập nhật tham số ứng dụng" : "Tạo tham số ứng dụng"} open={modalOpen} okText="Lưu" cancelText="Hủy" confirmLoading={saving} onOk={save} onCancel={() => setModalOpen(false)} destroyOnClose>
      <Form form={form} layout="vertical">
        {isSuperAdmin && <Form.Item name="userId" label="Người dùng" rules={[{ required: true, message: "Chọn người dùng hoặc hệ thống toàn cục" }]}><Select aria-label="Người dùng" showSearch optionFilterProp="label" options={userOptions} /></Form.Item>}
        <Form.Item name="parType" label="Loại tham số" rules={[{ required: true, whitespace: true, message: "Nhập loại tham số" }]}><Input maxLength={100} placeholder="Ví dụ: VIDEO" /></Form.Item>
        <Form.Item name="parCode" label="Mã tham số" rules={[{ required: true, whitespace: true, message: "Nhập mã tham số" }]}><Input maxLength={100} placeholder="Ví dụ: QUALITY" /></Form.Item>
        <Form.Item name="parName" label="Tên tham số" rules={[{ required: true, whitespace: true, message: "Nhập tên tham số" }]}><Input maxLength={255} /></Form.Item>
        <Form.Item name="parValue" label="Giá trị tham số"><Input.TextArea rows={3} /></Form.Item>
        <Form.Item name="description" label="Mô tả"><Input.TextArea rows={3} /></Form.Item>
      </Form>
    </Modal>
  </>;
}
